import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import StarField from "@/components/StarField";
import TopBar from "@/components/TopBar";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirect = params.get("redirect") || "/dashboard";
  const intent = params.get("intent"); // "membership" | null
  const isMembership = intent === "membership";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Access granted");

      // Membership intent: send them straight to Stripe (unless already a member)
      if (isMembership && u.role === "user") {
        if (u.membership_active) {
          toast.success("You're already in the Inner Circle");
          navigate("/dashboard");
          return;
        }
        try {
          const { data } = await api.post("/checkout/membership", {
            origin_url: window.location.origin,
          });
          window.location.href = data.url;
          return;
        } catch (err) {
          toast.error(err.response?.data?.detail || "Checkout failed. Continue from your dashboard.");
          navigate("/membership");
          return;
        }
      }

      if (u.role === "admin") navigate("/admin");
      else if (u.role === "influencer") navigate("/influencer/dashboard");
      else navigate(redirect);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally { setLoading(false); }
  };

  return (
    <div className="relative min-h-screen">
      <StarField count={120} />
      <TopBar />
      <main className="relative z-20 flex items-center justify-center px-6 py-16">
        <div className="glass-strong rounded-sm p-8 sm:p-12 w-full max-w-md">
          <div className="text-center">
            <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-cyan-400/80 mb-2">
              — {isMembership ? "return · join the circle" : "enter the circle"} —
            </div>
            <h1 className="font-display text-3xl font-black uppercase">
              {isMembership ? "Sign In To Pay" : "Sign In"}
            </h1>
            {isMembership && (
              <p className="font-mono text-[11px] uppercase tracking-widest text-white/50 mt-4">
                After sign-in you'll go straight to Stripe · $10 / month
              </p>
            )}
          </div>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" data-testid="login-email-input" className="w-full px-4 py-3 bg-black/60 border border-white/15 rounded-sm font-mono text-sm focus:outline-none focus:border-cyan-400/80" />
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" data-testid="login-password-input" className="w-full px-4 py-3 bg-black/60 border border-white/15 rounded-sm font-mono text-sm focus:outline-none focus:border-cyan-400/80" />
            <button type="submit" disabled={loading} data-testid="login-submit-btn" className="gate w-full py-3 bg-white text-black font-display font-bold uppercase tracking-[0.3em] text-xs rounded-sm hover:scale-[1.02] transition disabled:opacity-50">
              {loading ? "..." : isMembership ? "Sign In & Pay $10" : "Enter"}
            </button>
          </form>
          <div className="text-center font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mt-6">
            <Link
              to={isMembership ? "/register?intent=membership" : "/register"}
              className="text-cyan-400 hover:underline"
              data-testid="login-go-register"
            >
              Create account
            </Link>
            <span className="mx-3 text-white/20">·</span>
            <Link
              to="/forgot-password"
              className="text-cyan-400 hover:underline"
              data-testid="login-forgot-password-link"
            >
              Forgot password?
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
